import { midiToNoteName, type NoteName } from "./guitarEngine";
import { pianoHandForMidi } from "./pianoPedagogy";
import type { PianoFingeredKey } from "./pianoFingeringEngine";
import { clampFinger } from "./practiceNoteModel";
import type { PracticeNoteVisualState } from "./practiceVisualTheme";
import type { TimedMusicXmlNote } from "./songEngine";

export function isSoundingSongNote(note: TimedMusicXmlNote | undefined): note is TimedMusicXmlNote {
  return Boolean(note && !note.isRest && note.midi > 0);
}

/** Todas las notas sonoras en el mismo instante que `index`. */
export function songNotesAtSameTime(
  notes: TimedMusicXmlNote[],
  index: number
): TimedMusicXmlNote[] {
  const anchor = notes[index];
  if (!isSoundingSongNote(anchor)) return [];
  return songNotesAtStartTime(notes, anchor.startTime);
}

/** Notas sonoras en un instante musical concreto (acorde / evento). */
export function songNotesAtStartTime(
  notes: TimedMusicXmlNote[],
  startTime: number
): TimedMusicXmlNote[] {
  if (startTime < 0) return [];
  return notes.filter(
    (n) => isSoundingSongNote(n) && Math.abs(n.startTime - startTime) < 1e-9
  );
}

/** Siguiente ataque rítmico estrictamente después de `afterStartTime`. */
export function nextSongStartTime(
  notes: TimedMusicXmlNote[],
  afterStartTime: number
): number | null {
  let best: number | null = null;
  for (const n of notes) {
    if (!isSoundingSongNote(n)) continue;
    if (n.startTime <= afterStartTime + 1e-9) continue;
    if (best === null || n.startTime < best) best = n.startTime;
  }
  return best;
}

/** Instantáneo activo: timeline en reproducción, o nota actual en aprendizaje. */
export function resolveSongPianoActiveStartTime(
  notes: TimedMusicXmlNote[],
  activeStartTime: number,
  currentNoteIndex: number
): number {
  if (activeStartTime >= 0) return activeStartTime;
  const anchor = notes[currentNoteIndex];
  if (isSoundingSongNote(anchor)) return anchor.startTime;
  return -1;
}

function stepFromMidi(midi: number): NoteName {
  return midiToNoteName(midi).note;
}

function pushFingeredKey(
  out: PianoFingeredKey[],
  seen: Set<number>,
  midi: number,
  finger: number,
  degree = "♪"
): void {
  if (seen.has(midi)) return;
  seen.add(midi);
  out.push({
    midi,
    finger: clampFinger(((finger - 1) % 5) + 1),
    hand: pianoHandForMidi(midi),
    degree,
    step: stepFromMidi(midi),
  });
}

/** Teclas a colorear: solo el evento activo (+ opcionalmente el siguiente en reposo). */
export function buildSongPianoFingering(
  notes: TimedMusicXmlNote[],
  activeStartTime: number,
  lookaheadGroups = 0
): PianoFingeredKey[] {
  const seen = new Set<number>();
  const out: PianoFingeredKey[] = [];
  let finger = 1;

  const addGroup = (startTime: number, degree: string) => {
    for (const n of songNotesAtStartTime(notes, startTime)) {
      pushFingeredKey(out, seen, n.midi, finger++, degree);
    }
  };

  if (activeStartTime >= 0) {
    addGroup(activeStartTime, "●");
    let t = activeStartTime;
    for (let g = 0; g < lookaheadGroups; g++) {
      const next = nextSongStartTime(notes, t);
      if (next === null) break;
      addGroup(next, "◦");
      t = next;
    }
  }

  return out.sort((a, b) => a.midi - b.midi);
}

/** Estados visuales solo del evento activo (no acumular aciertos pasados). */
export function buildSongPianoNoteStates(
  notes: TimedMusicXmlNote[],
  activeStartTime: number,
  noteFeedback: Record<number, "correct" | "wrong">
): Map<number, PracticeNoteVisualState> {
  const states = new Map<number, PracticeNoteVisualState>();
  if (activeStartTime < 0) return states;

  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (!isSoundingSongNote(n) || Math.abs(n.startTime - activeStartTime) > 1e-9) continue;
    const fb = noteFeedback[i];
    if (fb === "correct") {
      states.set(n.midi, "hit");
    } else if (fb === "wrong") {
      states.set(n.midi, "miss");
    } else {
      states.set(n.midi, "expected");
    }
  }

  return states;
}

export function primarySongActiveMidi(
  notes: TimedMusicXmlNote[],
  activeStartTime: number
): number | null {
  const group = songNotesAtStartTime(notes, activeStartTime);
  return group[0]?.midi ?? null;
}

/** @deprecated Usar nextSongStartTime + buildSongPianoFingering. */
export function songLookaheadGroups(
  notes: TimedMusicXmlNote[],
  fromIndex: number,
  maxGroups: number
): TimedMusicXmlNote[][] {
  const anchor = notes[fromIndex];
  if (!anchor) return [];
  const times = new Set<number>();
  const groups: TimedMusicXmlNote[][] = [];
  for (let i = fromIndex + 1; i < notes.length && groups.length < maxGroups; i++) {
    const n = notes[i];
    if (!isSoundingSongNote(n) || times.has(n.startTime)) continue;
    times.add(n.startTime);
    groups.push(songNotesAtSameTime(notes, i));
  }
  return groups;
}
